import TooltipWrapper from '@/components/base/TooltipWrapper/TooltipWrapper';
import { FilterType } from '@/models/filter/FilterType';
import Badge from '@/storybook/components/Badge/Badge';
import Button from '@/storybook/components/Button/Button';
import TrashCanIcon from '@/storybook/icons/TrashCanIcon';
import React, { type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useFilterCounts } from '../hooks/useFilterCounts';
import { useVisibleFilterSections } from '../hooks/useVisibleFilterSections';
import { SectionId } from '../types/filterDraft.types';
import styles from './FiltersSidebar.module.css';

const buildSectionAnchorId = (sectionId: SectionId): string => `filter-section-${sectionId}`;

const SIDEBAR_TOOLTIP_STYLE: CSSProperties = {
  zIndex: 'calc(var(--z-above-all) + 1)',
};

interface FiltersSidebarProps {
  activeSection: SectionId;
  setActiveSection: (id: SectionId) => void;
  handleClearSection: (keys: (FilterType | FilterType[])[]) => void;
}

export const FiltersSidebar: React.FC<FiltersSidebarProps> = ({
  activeSection,
  setActiveSection,
  handleClearSection,
}) => {
  const { t } = useTranslation();
  const visibleSections = useVisibleFilterSections();
  const { getActiveFiltersCount, getSelectedItemsCountSplit } = useFilterCounts();

  const handleSectionClick = (sectionId: SectionId) => {
    setActiveSection(sectionId);
  };

  const handleSectionKeyDown = (event: React.KeyboardEvent<HTMLDivElement>, sectionId: SectionId) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setActiveSection(sectionId);
    }
  };

  const handleClearSectionClick = (e: React.MouseEvent, keys: (FilterType | FilterType[])[]) => {
    e.stopPropagation();
    handleClearSection(keys);
  };

  const handleStopPropagation = (e: React.KeyboardEvent) => {
    e.stopPropagation();
  };

  return (
    <div className={styles.filterSidebar}>
      {visibleSections.map(section => {
        const activeCount = getActiveFiltersCount(section.filterKeys);
        const { active: activeItemsCount, total: totalItemsCount } = getSelectedItemsCountSplit(section.filterKeys);
        const badgeLabel =
          activeItemsCount === totalItemsCount ? `${totalItemsCount}` : `${activeItemsCount}/${totalItemsCount}`;
        const hasActiveFilters = activeCount > 0;

        if (section.disabled) {
          const anchorId = buildSectionAnchorId(section.id);
          return (
            <React.Fragment key={section.id}>
              <div
                id={anchorId}
                className={`${styles.filterSidebarItem} ${styles.disabled}`}
                aria-disabled="true"
                data-testid={section.testId}
              >
                <div className={styles.filterSidebarItemContent}>
                  <div className={styles.filterSidebarItemLeft}>
                    {section.icon && <span className={styles.filterSidebarIcon}>{section.icon}</span>}
                    <span className={styles.filterSidebarItemText}>{t(section.labelKey)}</span>
                  </div>

                  {section.comingSoon && (
                    <Badge color="blue" variant="light" size="S">
                      {t('global.filters.comingSoonBadgeLabel')}
                    </Badge>
                  )}
                </div>
              </div>

              {section.tooltipKey &&
                createPortal(
                  <TooltipWrapper anchorSelect={`#${anchorId}`} place="top-end" style={SIDEBAR_TOOLTIP_STYLE}>
                    {t(section.tooltipKey)}
                  </TooltipWrapper>,
                  document.body,
                )}
            </React.Fragment>
          );
        }

        return (
          <div
            key={section.id}
            className={`${styles.filterSidebarItem} ${activeSection === section.id ? styles.active : ''}`}
            onClick={() => handleSectionClick(section.id)}
            onKeyDown={e => handleSectionKeyDown(e, section.id)}
            role="button"
            tabIndex={0}
            data-testid={section.testId}
          >
            <div className={styles.filterSidebarItemContent}>
              <div className={styles.filterSidebarItemLeft}>
                {section.icon && <span className={styles.filterSidebarIcon}>{section.icon}</span>}
                <span className={styles.filterSidebarItemText}>{t(section.labelKey)}</span>
              </div>

              {hasActiveFilters && section.id !== 'recommendations' && (
                <div className={styles.filterSidebarItemRight}>
                  <span className={styles.filterBadge}>{badgeLabel}</span>

                  <Button
                    type="button"
                    variant="text"
                    size="S"
                    iconOnly
                    icon={<TrashCanIcon />}
                    className={styles.clearSectionButton}
                    onClick={e => handleClearSectionClick(e, section.filterKeys)}
                    onKeyDown={handleStopPropagation}
                  />
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
